import { memoize } from 'lodash';
import { createSelector } from 'reselect';
import { State } from '.';
import {
    PatientPortalData,
    PatientPortalDataAction,
    PatientPortalDataStatus,
    PatientPortalDataType
} from '../../models/patient-portal-data';
import {
    PatientRx,
    RxStatus
} from '../../models/patient-rx';
import { getUTCCurrentDateRange } from '../../utils/formatter';

const byPassSet = new Set([
    PatientPortalDataType.DrugAbuseHistory,
    PatientPortalDataType.FallHistory
]);

const getPatientPortalDataHashSelector = createSelector(
    (state: State) => state.byCode,
    (byCode: Dictionary<PatientPortalData>) => memoize((
        type: Undefinable<PatientPortalDataType>
    ) => memoize((
        preHash: Undefinable<Dictionary<PatientPortalDataMerge>>
    ) => {
        const keySet = new Set(Object.keys(preHash || {}));
        const [startDate, endDate] = getUTCCurrentDateRange('YYYY-MM-DDTHH:mm:ss');
        return Object
            .keys(byCode)
            .filter(x => type === byCode[x]!.type && PatientPortalDataStatus.Pending === byCode[x]!.status && (
                PatientPortalDataAction.Created === byCode[x]!.action ||
                PatientPortalDataAction.Deleted === byCode[x]!.action || (
                    PatientPortalDataAction.Updated === byCode[x]!.action && (
                        byCode[x]!.type !== PatientPortalDataType.Medication || (
                            startDate <= byCode[x]!.createdDate &&
                            endDate > byCode[x]!.createdDate
                        )
                    )
                )
            ))
            .reduce(
                (prevValue, x) => {
                    const id = byCode[x]!.relatedId || '';
                    // get created or matched with current entities
                    if (
                        byPassSet.has(byCode[x]!.type) ||
                        !id ||
                        byCode[x]!.action === PatientPortalDataAction.Created ||
                        keySet.has(id)
                    ) {
                        let priority: Undefinable<number>;
                        let ref: Undefinable<boolean>;
                        let refId: Undefinable<DictionaryKey>;
                        if (type === PatientPortalDataType.Medication) {
                            const model = byCode[x]!.dataModel as PatientRx;
                            refId = model.medId;
                            priority = model.status === RxStatus.NotTaking ? 1 : 0;
                            ref = model.status === RxStatus.Taking || model.status === RxStatus.NotTaking;
                        }
                        prevValue[id] = {
                            id: id,
                            refId: refId,
                            action: byCode[x]!.action,
                            date: byCode[x]!.createdDate,
                            priority: priority,
                            ref: ref
                        } as PatientPortalDataMerge;
                    }
                    return prevValue;
                },
                {} as Dictionary<PatientPortalDataMerge>
            );
    }))
);

const getPatientPortalDataNewsSelector = createSelector(
    (state: State) => state.byCode,
    (byCode: Dictionary<PatientPortalData>) => memoize((
        type: Undefinable<PatientPortalDataType>
    ) => Object
        .keys(byCode)
        .map(x => byCode[x]!)
        .filter(x =>
            x.type === type &&
            x.status === PatientPortalDataStatus.Pending &&
            x.action === PatientPortalDataAction.Created
        )
        .sort((a, b) => a.relatedCreatedDate > b.relatedCreatedDate ? 1 : -1)
    )
);

const getPatientPortalDataNewCodesSelector = createSelector(
    (state: State) => state.byCode,
    (byCode: Dictionary<PatientPortalData>) => memoize((
        type: Undefinable<PatientPortalDataType>
    ) => Object
        .keys(byCode)
        .filter(x =>
            byCode[x]!.type === type &&
            byCode[x]!.status === PatientPortalDataStatus.Pending &&
            byCode[x]!.action === PatientPortalDataAction.Created
        )
        .sort((a, b) => byCode[a]!.relatedCreatedDate > byCode[b]!.relatedCreatedDate ? 1 : -1)
    )
);

const isPendingExistedSelector = createSelector(
    (state: State) => state.byCode,
    (byCode: Dictionary<PatientPortalData>) => memoize((
        type: Undefinable<PatientPortalDataType>
    ) => Object
        .keys(byCode)
        .filter(x =>
            byCode[x]!.type === type &&
            byCode[x]!.status === PatientPortalDataStatus.Pending &&
            byCode[x]!.action !== PatientPortalDataAction.Deleted
        )
        .length > 0
    )
);

export const selectors = {
    getLoading: (
        state: State,
        loadingKey: string
    ): boolean => state.loading[loadingKey] || false,
    getPatientPortalData: (
        state: State,
        type: Undefinable<PatientPortalDataType>,
        relatedId?: string | number,
        code?: Undefinable<string | number>
    ): Undefinable<PatientPortalData> => {
        const patientPortalData = state.byCode[code || `${type || ''}---${relatedId || ''}`];
        if (patientPortalData && patientPortalData.status === PatientPortalDataStatus.Pending) {
            if (PatientPortalDataType.Medication === patientPortalData.type) {
                const [startDate, endDate] = getUTCCurrentDateRange('YYYY-MM-DDTHH:mm:ss');
                if (PatientPortalDataAction.Created === patientPortalData.action || (
                    PatientPortalDataAction.Updated === patientPortalData.action &&
                    startDate <= patientPortalData.createdDate &&
                    endDate > patientPortalData.createdDate
                )) {
                    return patientPortalData;
                }
            } else {
                return patientPortalData;
            }
        }
        return undefined;
    },
    getPatientPortalDataHash: (
        state: State,
        type: Undefinable<PatientPortalDataType>,
        preHash?: Dictionary<PatientPortalDataMerge>
    ): Dictionary<PatientPortalDataMerge> => getPatientPortalDataHashSelector(state)(type)(preHash),
    getPatientPortalDataNews: (
        state: State,
        type: Undefinable<PatientPortalDataType>
    ): PatientPortalData[] => getPatientPortalDataNewsSelector(state)(type),
    getPatientPortalDataNewCodes: (
        state: State,
        type: Undefinable<PatientPortalDataType>
    ): string[] => getPatientPortalDataNewCodesSelector(state)(type),
    isDeleted: (
        state: State,
        type: Undefinable<PatientPortalDataType>,
        relatedId?: string
    ): boolean => {
        const patientPortalData = state.byCode[`${type || ''}---${relatedId || ''}`];
        return !!(patientPortalData && patientPortalData.action === PatientPortalDataAction.Deleted);
    },
    isPendingExisted: (
        state: State,
        type: Undefinable<PatientPortalDataType>
    ): boolean => isPendingExistedSelector(state)(type)
};
